import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "@/contexts/AuthContext";
import { supabase, handleSupabaseError, getSubscriptionData, updateSubscriptionData } from "@/lib/supabase";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Mail, Lock, User, Building, CreditCard, Eye, EyeOff, LogOut, Check, X, Github, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Database } from '@/types/supabase';
import { subscriptionPlans } from "@/utils/subscriptionPlans";

type ProfileFormValues = {
  name: string;
  email: string;
  company: string;
};

type PasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type NotificationPreferences = {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
};

type NotificationKey = keyof NotificationPreferences;

interface PasswordValues {
  currentPassword?: string;
  newPassword: string;
}

// Add a type for the profile
interface UserProfile {
  id: string;
  full_name: string;
  company: string;
  avatar_url?: string;
}

// Helper to get plan details
const getPlanDetails = (planId: string) => subscriptionPlans.find(p => p.id === planId);

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const session_id = searchParams.get('session_id');
  const tabParam = searchParams.get('tab');
  const { user, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (session_id) return "subscription";
    if (tabParam) return tabParam;
    return "profile";
  });
  const [subscription, setSubscription] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    number: false,
    special: false
  });
  
  // Add notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
    emailNotifications: false,
    pushNotifications: false,
    smsNotifications: false
  });
  
  // Load notification preferences from user metadata on mount
  useEffect(() => {
    if (user?.user_metadata?.notification_preferences) {
      setNotificationPreferences(user.user_metadata.notification_preferences);
    }
  }, [user]);
  
  // Load subscription data
  useEffect(() => {
    const loadSubscriptionData = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoading(true);
        const data = await getSubscriptionData(user.id);
        setSubscription(data || { status: 'inactive' }); // Set default state if no subscription
      } catch (error) {
        console.error('Error loading subscription data:', error);
        // Only show error toast for unexpected errors
        if (error instanceof Error && error.message !== 'Resource not found') {
          toast({
            title: "Error",
            description: "Failed to load subscription data. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptionData();
  }, [user?.id]);
  
  // Handle subscription success
  useEffect(() => {
    if (session_id) {
      // Clear the session_id from URL
      navigate('/settings', { replace: true });
      
      // Show success message with plan details
      const planName = user?.user_metadata?.subscription_plan || "Premium";
      toast({
        title: "Subscription Activated",
        description: `Thank you for subscribing to MarketEye ${planName}! Your subscription has been successfully activated.`,
        duration: 5000,
      });
    }
  }, [session_id, navigate, user]);
  
  // Form setup
  const profileForm = useForm<ProfileFormValues>({
    defaultValues: {
      name: "",
      email: "",
      company: "",
    }
  });
  const notificationsForm = useForm();
  const passwordForm = useForm<PasswordFormValues>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });
  
  // Add a type for the profile
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Fetch the profile from the profiles table
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      setProfileLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, company, avatar_url')
        .eq('id', user.id)
        .single();
      if (!error && data) {
        setProfile(data);
        profileForm.reset({
          name: data.full_name || '',
          email: user.email || '',
          company: data.company || '',
        });
      }
      setProfileLoading(false);
    };
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Update form when profile changes
  useEffect(() => {
    if (profile) {
      profileForm.reset({
        name: profile.full_name || '',
        email: user?.email || '',
        company: profile.company || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // Handle sidebar state on mount and location changes
  useEffect(() => {
    // Reset sidebar state when navigating away from settings
    if (location.pathname !== '/settings') {
      setSidebarOpen(true);
    }
  }, [location.pathname]);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  // Update handleSaveProfile to update the profiles table
  const handleSaveProfile = async (data: ProfileFormValues) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.name,
          company: data.company,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (error) {
        handleSupabaseError(error);
      } else {
        setProfile((prev) => prev ? { ...prev, full_name: data.name, company: data.company } : prev);
        toast({
          title: "Profile Updated",
          description: "Your profile information has been updated successfully.",
        });
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleSaveNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          notification_preferences: notificationPreferences
        }
      });

      if (error) {
        handleSupabaseError(error);
      }

      toast({
        title: "Notification Settings Updated",
        description: "Your notification preferences have been saved.",
      });
    } catch (error) {
      console.error("Notification settings update error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update notification settings. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  const handleDeleteAccount = async () => {
    if (!user) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_user');
      if (error) {
        handleSupabaseError(error);
      }
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Delete account error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };
  
  // Get the list of linked providers from user metadata
  const linkedProviders = user?.app_metadata?.providers || [];
  const isEmailLinked = user?.email && (linkedProviders.includes('email') || linkedProviders.length === 0);
  const isGoogleLinked = linkedProviders.includes('google');
  const isGithubLinked = linkedProviders.includes('github');

  console.log('User metadata:', {
    app_metadata: user?.app_metadata,
    user_metadata: user?.user_metadata,
    linkedProviders,
    isEmailLinked,
    isGoogleLinked,
    isGithubLinked
  });

  // Add state to track if user is social-only
  const isSocialOnlyUser = isEmailLinked ? false : (isGoogleLinked || isGithubLinked);

  // Password validation effect
  useEffect(() => {
    const newPassword = passwordForm.getValues('newPassword');
    setPasswordValidation({
      length: newPassword.length >= 8,
      number: /\d/.test(newPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
    });
  }, [passwordForm.watch('newPassword')]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const handleChangePassword = async (data: PasswordFormValues) => {
    if (!user) return;
    
    // Validate password requirements
    const newPassword = data.newPassword;
    const isValidPassword = 
      newPassword.length >= 8 && 
      /\d/.test(newPassword) && 
      /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!isValidPassword) {
      toast({
        title: "Invalid Password",
        description: "Please ensure your new password meets all requirements.",
        variant: "destructive",
      });
      return;
    }

    // Validate password confirmation
    if (data.newPassword !== data.confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "New password and confirmation password must match.",
        variant: "destructive",
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      if (isSocialOnlyUser) {
        // Add email provider and set password
        const { error: providerError } = await supabase.rpc('add_email_provider' as any, {
          new_password: data.newPassword
        });
        if (providerError) throw providerError;
      } else {
        // First verify current password
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user.email!,
          password: data.currentPassword
        });

        if (signInError) {
          throw new Error('Current password is incorrect');
        }

        // Change password if current password is correct
        const { error } = await supabase.auth.updateUser({
          password: data.newPassword
        });
        if (error) throw error;
      }

      setShowChangePasswordDialog(false);
      toast({
        title: isSocialOnlyUser ? "Password Added" : "Password Updated",
        description: isSocialOnlyUser 
          ? "You can now sign in using your email and password"
          : "Your password has been changed successfully",
      });

      // Force a session refresh to ensure all components get the updated user data
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
    } catch (error: any) {
      console.error("Password change error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleProviderClick = async (provider: 'google' | 'github') => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/settings`
        }
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error connecting provider:', error);
    }
  };

  const handleNotificationChange = (key: NotificationKey) => (checked: boolean) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };
  
  const planDetails = subscription ? getPlanDetails(subscription.plan_id) : null;
  const planName = planDetails ? planDetails.name : 'No Plan';
  const planPrice = planDetails ? (subscription.interval === 'year' ? planDetails.annualPrice : planDetails.monthlyPrice) : null;
  const planPriceDisplay = planPrice !== null ? `$${planPrice}/${subscription.interval === 'year' ? 'year' : 'month'}` : '';
  const renewalDate = subscription && subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : '';
  const planStatus = subscription ? subscription.status : 'inactive';
  
  // Update activeTab if tabParam changes
  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Header toggleSidebar={toggleSidebar} />
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="flex-1">
          <div className="max-w-[900px] mx-auto py-6 px-6">
            <div>
              <h1 className="text-2xl font-bold">Settings</h1>
            </div>

            <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="mt-6">
              <div className="px-0">
                <TabsList className="grid w-full grid-cols-3 lg:max-w-[450px] h-10 bg-muted">
                  <TabsTrigger 
                    value="profile" 
                    className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none"
                  >
                    Profile
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notifications" 
                    className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none"
                  >
                    Notifications
                  </TabsTrigger>
                  <TabsTrigger 
                    value="subscription" 
                    className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none"
                  >
                    Subscription
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="mt-8 space-y-6 p-0">
              <TabsContent value="profile">
                  <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Profile Information</CardTitle>
                    <CardDescription>
                      Update your account details and personal information.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {profileLoading ? (
                      <div className="py-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        <div>Loading profile...</div>
                      </div>
                    ) : (
                      <Form {...profileForm}>
                        <form onSubmit={profileForm.handleSubmit(handleSaveProfile)} className="space-y-6">
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <FormLabel htmlFor="name">Full Name</FormLabel>
                                <div className="relative">
                                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                      <FormField
                                        control={profileForm.control}
                                        name="name"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormControl>
                                      <Input
                                            {...field}
                                        className="pl-10"
                                            placeholder="Enter your full name"
                                          />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                  />
                                </div>
                              </div>
                              
                              <div className="space-y-2">
                                <FormLabel htmlFor="email">Email Address</FormLabel>
                                <div className="relative">
                                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                      <FormField
                                        control={profileForm.control}
                                        name="email"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormControl>
                                      <Input
                                            {...field}
                                        type="email"
                                        className="pl-10"
                                            placeholder="Enter your email"
                                            disabled
                                          />
                                            </FormControl>
                                            <FormDescription>
                                              Email address cannot be changed
                                            </FormDescription>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                  />
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <FormLabel htmlFor="company">Company Name</FormLabel>
                              <div className="relative">
                                <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <FormField
                                      control={profileForm.control}
                                      name="company"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormControl>
                                    <Input
                                          {...field}
                                        className="pl-10"
                                          placeholder="Enter your company name (optional)"
                                        />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <FormLabel htmlFor="password">Password</FormLabel>
                              <div className="relative">
                                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                <Input
                                  id="password"
                                  type="password"
                                  value="********"
                                  disabled
                                  className="pl-10"
                                />
                              </div>
                              <FormDescription>
                                    Password can be changed in the Security section below
                              </FormDescription>
                            </div>
                          </div>
                          
                          <Button type="submit">Save Changes</Button>
                        </form>
                      </Form>
                    )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Linked Accounts</CardTitle>
                        <CardDescription>
                          Manage your connected accounts and sign-in methods.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center space-x-4">
                              <Mail className="h-5 w-5 text-gray-500" />
                              <div>
                                <p className="font-medium">Email & Password</p>
                                <p className="text-sm text-muted-foreground">{user?.email}</p>
                              </div>
                            </div>
                            {isEmailLinked ? (
                              <Badge variant="outline" className="bg-green-50">
                                <Check className="h-4 w-4 mr-1 text-green-600" />
                                <span className="text-green-600">Connected</span>
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50">
                                <X className="h-4 w-4 mr-1 text-gray-500" />
                                <span className="text-gray-500">Not Connected</span>
                              </Badge>
                            )}
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center space-x-4">
                              <svg viewBox="0 0 24 24" className="h-5 w-5 text-[#4285F4]">
                                <path
                                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                  fill="#34A853"
                                />
                                <path
                                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                  fill="#FBBC05"
                                />
                                <path
                                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                  fill="#EA4335"
                                />
                              </svg>
                              <div>
                                <p className="font-medium">Google</p>
                                <p className="text-sm text-muted-foreground">Sign in with Google</p>
                              </div>
                            </div>
                            {isGoogleLinked ? (
                              <Badge variant="outline" className="bg-green-50">
                                <Check className="h-4 w-4 mr-1 text-green-600" />
                                <span className="text-green-600">Connected</span>
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50">
                                <X className="h-4 w-4 mr-1 text-gray-500" />
                                <span className="text-gray-500">Not Connected</span>
                              </Badge>
                            )}
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center space-x-4">
                              <Github className="h-5 w-5 text-gray-900" />
                              <div>
                                <p className="font-medium">GitHub</p>
                                <p className="text-sm text-muted-foreground">Sign in with GitHub</p>
                              </div>
                            </div>
                            {isGithubLinked ? (
                              <Badge variant="outline" className="bg-green-50">
                                <Check className="h-4 w-4 mr-1 text-green-600" />
                                <span className="text-green-600">Connected</span>
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50">
                                <X className="h-4 w-4 mr-1 text-gray-500" />
                                <span className="text-gray-500">Not Connected</span>
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Security</CardTitle>
                        <CardDescription>
                          Manage your password and security preferences.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-4">
                            <Lock className="h-5 w-5 text-gray-500" />
                            <div>
                              <p className="font-medium">Password</p>
                              <p className="text-sm text-muted-foreground">
                                {isSocialOnlyUser 
                                  ? "Add a password to enable email & password login"
                                  : "Change your account password"}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => setShowChangePasswordDialog(true)}
                          >
                            {isSocialOnlyUser ? "Add Password" : "Change Password"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Separator className="my-8" />
                    
                    <div>
                      <h3 className="text-lg font-medium mb-4">Danger Zone</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h4 className="font-medium">Delete Account</h4>
                            <p className="text-sm text-muted-foreground">
                              Permanently delete your account and all data
                            </p>
                          </div>
                          <Button 
                            variant="destructive" 
                            onClick={() => setShowDeleteDialog(true)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "Deleting..." : "Delete Account"}
                          </Button>
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h4 className="font-medium">Log Out</h4>
                            <p className="text-sm text-muted-foreground">
                              Sign out of your account on this device
                            </p>
                          </div>
                          <Button variant="outline" onClick={handleLogout}>
                            <LogOut className="mr-2 h-4 w-4" />
                            Log Out
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
              </TabsContent>
              
              <TabsContent value="notifications">
                <Card>
                  <CardHeader>
                    <CardTitle>Notification Preferences</CardTitle>
                    <CardDescription>
                      Manage how and when you receive notifications.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Form {...notificationsForm}>
                      <form onSubmit={handleSaveNotifications} className="space-y-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div>
                              <h4 className="font-medium">Email Notifications</h4>
                              <p className="text-sm text-muted-foreground">
                                Receive notifications via email
                              </p>
                            </div>
                            <Switch 
                                id="emailNotifications"
                                checked={notificationPreferences.emailNotifications}
                                onCheckedChange={handleNotificationChange('emailNotifications')}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div>
                                <h4 className="font-medium">Push Notifications</h4>
                              <p className="text-sm text-muted-foreground">
                                  Receive notifications via push notifications
                              </p>
                            </div>
                            <Switch 
                                id="pushNotifications"
                                checked={notificationPreferences.pushNotifications}
                                onCheckedChange={handleNotificationChange('pushNotifications')}
                            />
                          </div>
                          
                          <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div>
                                <h4 className="font-medium">SMS Notifications</h4>
                              <p className="text-sm text-muted-foreground">
                                  Receive notifications via SMS
                              </p>
                            </div>
                            <Switch 
                                id="smsNotifications"
                                checked={notificationPreferences.smsNotifications}
                                onCheckedChange={handleNotificationChange('smsNotifications')}
                            />
                          </div>
                        </div>
                        
                        <Button type="submit">Save Preferences</Button>
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="subscription">
                <Card>
                  <CardHeader>
                    <CardTitle>Subscription Management</CardTitle>
                    <CardDescription>
                      Manage your subscription plan and billing information.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="p-4 border rounded-lg space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="font-medium">Current Plan</h3>
                          <Badge variant="outline" className="text-accent bg-accent/10">{planStatus === 'active' ? 'Active' : planStatus.charAt(0).toUpperCase() + planStatus.slice(1)}</Badge>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <h4 className="text-lg font-bold">{planName}</h4>
                            <span className="text-lg font-bold">{planPriceDisplay}</span>
                          </div>
                          {renewalDate && (
                            <p className="text-sm text-muted-foreground">
                              Your next billing date is {renewalDate}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => navigate("/subscription")}>Change Plan</Button>
                          <Button variant="ghost" onClick={() => {
                            toast({
                              title: "Demo Account",
                              description: "Subscription cannot be cancelled in the demo.",
                            });
                          }}>Cancel Subscription</Button>
                        </div>
                      </div>
                      
                      <div className="p-4 border rounded-lg space-y-4">
                        <h3 className="font-medium">Payment Method</h3>
                        
                        <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-md">
                          <CreditCard className="h-6 w-6" />
                          <div>
                            <p className="font-medium">
                              •••• •••• •••• 4242
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Expires 12/24
                            </p>
                          </div>
                        </div>
                        
                        <Button variant="outline" onClick={() => {
                          toast({
                            title: "Demo Account",
                            description: "Payment methods cannot be changed in the demo.",
                          });
                        }}>
                          Update Payment Method
                        </Button>
                      </div>
                      
                      <div className="p-4 border rounded-lg space-y-4">
                        <h3 className="font-medium">Billing History</h3>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-2 border-b">
                            <div>
                              <p className="font-medium">Professional Plan</p>
                              <p className="text-sm text-muted-foreground">June 15, 2023</p>
                            </div>
                            <span>$79.00</span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b">
                            <div>
                              <p className="font-medium">Professional Plan</p>
                              <p className="text-sm text-muted-foreground">May 15, 2023</p>
                            </div>
                            <span>$79.00</span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <div>
                              <p className="font-medium">Professional Plan</p>
                              <p className="text-sm text-muted-foreground">April 15, 2023</p>
                            </div>
                            <span>$79.00</span>
                          </div>
                        </div>
                        
                        <Button variant="link" className="p-0">View All Invoices</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your account
              and remove all associated data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <AlertDialogContent className="sm:max-w-[425px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSocialOnlyUser ? "Add Password Authentication" : "Change Password"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isSocialOnlyUser 
                ? "Set up a password to enable email & password login in addition to your social login."
                : "Enter your current password and choose a new password for your account."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(handleChangePassword)} className="space-y-4">
              {!isSocialOnlyUser && (
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Current Password</FormLabel>
                        <Button
                          variant="link"
                          className="px-0 text-sm text-accent hover:underline"
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              await supabase.auth.signOut();
                              const { error } = await supabase.auth.resetPasswordForEmail(user?.email || '', {
                                redirectTo: `${window.location.origin}/auth`
                              });
                              if (error) throw error;
                              toast({
                                title: "Password Reset Email Sent",
                                description: "Please check your email for password reset instructions. You will need to log in again.",
                              });
                              setShowChangePasswordDialog(false);
                              navigate('/auth');
                            } catch (error: any) {
                              toast({
                                title: "Error",
                                description: error.message || "Failed to send reset email. Please try again.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          Forgot Password?
                        </Button>
                      </div>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showCurrentPassword ? "text" : "password"}
                            {...field}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                passwordForm.handleSubmit(handleChangePassword)();
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowCurrentPassword((v) => !v)}
                            tabIndex={-1}
                          >
                            {showCurrentPassword ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
              
              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isSocialOnlyUser ? "Password" : "New Password"}</FormLabel>
                    <TooltipProvider>
                      <Tooltip 
                        open={isPasswordFocused}
                        delayDuration={0}
                      >
                        <TooltipTrigger asChild>
                          <div className="relative">
                            <FormControl>
                              <Input
                                type={showNewPassword ? "text" : "password"}
                                {...field}
                                onFocus={() => setIsPasswordFocused(true)}
                                onBlur={() => setIsPasswordFocused(false)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    passwordForm.handleSubmit(handleChangePassword)();
                                  }
                                }}
                              />
                            </FormControl>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              tabIndex={-1}
                            >
                              {showNewPassword ? (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="right" 
                          align="start" 
                          sideOffset={15}
                          className="z-50"
                        >
                          <div className="space-y-2 p-3">
                            <p className="font-semibold">Password must:</p>
                            <ul className="space-y-1">
                              <li className={`flex items-center gap-2 ${passwordValidation.length ? 'text-green-500' : 'text-red-500'}`}>
                                {passwordValidation.length ? <Check size={16} /> : <X size={16} />}
                                Be at least 8 characters long
                              </li>
                              <li className={`flex items-center gap-2 ${passwordValidation.number ? 'text-green-500' : 'text-red-500'}`}>
                                {passwordValidation.number ? <Check size={16} /> : <X size={16} />}
                                Include at least one number
                              </li>
                              <li className={`flex items-center gap-2 ${passwordValidation.special ? 'text-green-500' : 'text-red-500'}`}>
                                {passwordValidation.special ? <Check size={16} /> : <X size={16} />}
                                Include at least one special character
                              </li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{isSocialOnlyUser ? "Confirm Password" : "Confirm New Password"}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          {...field}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              passwordForm.handleSubmit(handleChangePassword)();
                            }
                          }}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={passwordForm.handleSubmit(handleChangePassword)}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isSocialOnlyUser ? "Adding..." : "Changing..."}
                </>
              ) : (
                isSocialOnlyUser ? 'Add Password' : 'Change Password'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;
